import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { NotificationType, ShipmentsListType } from '@shared-features/enums/shared-enums.enum';
import { NotificationItem } from '../models/notification-item.model';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { NotificationApi } from './notification-api.service';
import { BidChatService } from '@features/bid-chat/services/bid-chat.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  public notification$ = new Subject<NotificationItem>();
  public markNotification$ = new Subject<NotificationItem>();
  // Shared by the navbar badge and the panel so both always show the same number.
  public unreadCount$ = new BehaviorSubject<number>(0);

  constructor(private router: Router, private notificationApi: NotificationApi, private bidChatService: BidChatService) { }

  showNotification(notification: NotificationItem) {
    this.notification$.next(notification);
  }

  refreshUnreadCount(): void {
    this.notificationApi.getUnreadNotificationCount()
      .pipe(catchError(() => EMPTY))
      .subscribe((count) => this.unreadCount$.next(count));
  }

  markAsRead(notifications: NotificationItem[]): Observable<unknown> {
    // Only stored notifications have a numeric id; a chat push that was never stored has nothing to mark.
    const unread = notifications.filter((notification) => !notification.isRead && Number.isFinite(+notification.id));
    if (!unread.length) { return of(null); }
    return this.notificationApi.markAsRead(unread.map((notification) => +notification.id)).pipe(
      tap(() => {
        unread.forEach((notification) => (notification.isRead = true));
        this.unreadCount$.next(Math.max(this.unreadCount$.value - unread.length, 0));
      })
    );
  }

  markAllAsRead(notifications: NotificationItem[]): Observable<unknown> {
    return this.notificationApi.markAllAsRead().pipe(
      tap(() => {
        notifications.forEach((notification) => (notification.isRead = true));
        this.unreadCount$.next(0);
      })
    );
  }

  navigateToNotificationTarget(notification: NotificationItem) {
    if (notification.type === NotificationType.Chat) {
      this.bidChatService.openChat({ conversationId: notification.conversationId, shipmentId: notification.elementId });
      return;
    }
    if (!notification.elementId) { return; }
    if (notification.type === NotificationType.Shipment) {
      this.router.navigate([RoutesUtil.ShipmentsView.url({ id: notification.elementId, type: ShipmentsListType.Outbound})]);
    } else if (notification.type === NotificationType.Invoice) {
      this.router.navigate([RoutesUtil.InvoicesDeliveryNotes.url({ invoiceId: notification.elementId })]);
    }
  }
}
